import React, { useContext, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel, openPanel } from '@state/actions/panelsActions'
import { useUpdateSchoolClass } from '@hooks/mutations/useUpdateSchoolClass'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Button from '@components/Button'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import Loader from '@components/Loader'
import { useUpdateBook } from '@hooks/mutations/useUpdateBook'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useSingleBook } from '@hooks/useSingleBook'
import { useSchoolGroups } from '@hooks/useSchoolGroups'
import { useUpsertSchoolGroup } from '@hooks/mutations/useUpsertSchoolGroup'
import { useUserSchoolClasses } from '@hooks/useUserSchoolClasses'
import { useDemoResources } from '@hooks/useDemoResources'
import { useUpdateDemoSchoolClass } from '@hooks/mutations/useUpdateDemoSchoolClass'
import { useUpdateDemoSchoolGroup } from '@hooks/mutations/useUpdateDemoSchoolGroup'
import { useNavigate } from 'react-router-dom'

const ShareBookPanel = () => {
  const navigate = useNavigate()
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { userId, selectedBookId, selectedBookTitle },
  } = state

  const { currentUser } = useCurrentUser()
  const {
    schoolClasses,
    isValidating: isValidatingClasses,
    error: classesError,
    isLoading: isLoadingClasses,
  } = useUserSchoolClasses()
  const {
    schoolGroups,
    isValidating: isValidatingGroups,
    error: groupsError,
    isLoading: isLoadingGroups,
  } = useSchoolGroups()
  const {
    demoSchoolClasses,
    demoSchoolGroups,
    isValidating: isValidatingDemoResources,
    error: demoResourcesError,
    isLoading: isLoadingDemoResources,
  } = useDemoResources()
  const { book } = useSingleBook(selectedBookId)
  const [selectedResourceType, setSelectedResourceType] = useState(
    SCHOOL_RESOURCE_TYPES.CLASS
  )
  const [schoolClassOptions, setSchoolClassOptions] = useState()
  const [schoolGroupOptions, setSchoolGroupOptions] = useState()
  const [demoSchoolClassOptions, setDemoSchoolClassOptions] = useState()
  const [demoSchoolGroupOptions, setDemoSchoolGroupOptions] = useState()
  const [selectedResourceValue, setSelectedResourceValue] = useState()
  const { register, handleSubmit, errors, setValue } = useForm()

  const isLoading =
    isValidatingDemoResources ||
    isValidatingClasses ||
    isValidatingGroups ||
    (selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
      ? isLoadingClasses
      : isLoadingGroups) ||
    isLoadingDemoResources
  const error =
    selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
      ? classesError
      : groupsError

  useEffect(() => {
    if (schoolClasses && !isValidatingClasses && !classesError) {
      const options = schoolClasses.map((schoolClass) => {
        const isSharedWithClass = schoolClass.books.some(
          (book) => book.id === selectedBookId
        )

        return {
          value: schoolClass.id,
          name: `${schoolClass.name} ${
            !isSharedWithClass ? '' : ' - boken är redan delad med klassen'
          }`,
          disabled: isSharedWithClass,
        }
      })
      setSchoolClassOptions(options)
    }
  }, [isValidatingClasses, classesError])

  useEffect(() => {
    if (schoolGroups && !isValidatingGroups && !classesError) {
      const options = schoolGroups.map((schoolGroup) => {
        const isSharedWithGroup = schoolGroup.books.some(
          (book) => book.id === selectedBookId
        )

        return {
          value: schoolGroup.id,
          name: `${schoolGroup.name} ${
            !isSharedWithGroup ? '' : ' - boken är redan delad med gruppen'
          }`,
          disabled: isSharedWithGroup,
        }
      })
      setSchoolGroupOptions(options)
    }
  }, [isValidatingGroups, classesError])

  // Demo stuff
  useEffect(() => {
    if (
      demoSchoolClasses &&
      !isValidatingDemoResources &&
      !demoResourcesError
    ) {
      const classOptions = demoSchoolClasses.map((schoolClass) => {
        const isSharedWithClass = schoolClass.books.some(
          (book) => book.id === selectedBookId
        )

        return {
          value: 'demo-' + schoolClass.id,
          name: `${schoolClass.name} ${
            !isSharedWithClass ? '' : ' - boken är redan delad med klassen'
          }`,
          disabled: isSharedWithClass,
        }
      })
      setDemoSchoolClassOptions(classOptions)

      const groupOptions = demoSchoolGroups.map((schoolGroup) => {
        const isSharedWithGroup = schoolGroup.books.some(
          (book) => book.id === selectedBookId
        )

        return {
          value: 'demo-' + schoolGroup.id,
          name: `${schoolGroup.name} ${
            !isSharedWithGroup ? '' : ' - boken är redan delad med gruppen'
          }`,
          disabled: isSharedWithGroup,
        }
      })
      setDemoSchoolGroupOptions(groupOptions)
    }
  }, [isValidatingDemoResources, demoResourcesError])

  useEffect(() => {
    if (schoolGroups && schoolClasses) {
      if (schoolGroups.length === 0 && schoolClasses.length !== 0) {
        setSelectedResourceType(SCHOOL_RESOURCE_TYPES.CLASS)
      }
      if (schoolClasses.length === 0 && schoolGroups.length !== 0) {
        setSelectedResourceType(SCHOOL_RESOURCE_TYPES.GROUP)
      }
    }
  }, [isValidatingGroups, isValidatingClasses])

  useEffect(() => {
    setValue('resource', undefined)
    setSelectedResourceValue()
  }, [selectedResourceType])

  const { post: postUpdateSchoolClass, isPosting: isPostingSchoolClass } =
    useUpdateSchoolClass()
  const { post: postUpdateSchoolGroup, isPosting: isPostingSchoolGroup } =
    useUpsertSchoolGroup()
  const {
    post: postUpdateDemoSchoolClass,
    isPosting: isPostingDemoSchoolClass,
  } = useUpdateDemoSchoolClass()
  const {
    post: postUpdateDemoSchoolGroup,
    isPosting: isPostingDemoSchoolGroup,
  } = useUpdateDemoSchoolGroup()
  const isPosting =
    selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
      ? isPostingSchoolClass
      : isPostingSchoolGroup
  const { post: postUpdateBook, isPostingUpdateBook } = useUpdateBook()

  const [userHasSubmitted, setUserHasSubmitted] = useState(false)

  const onSubmit = async (data) => {
    if (userHasSubmitted) return

    setUserHasSubmitted(true)

    const bookIsInBookCase = currentUser.books.some(
      (book) => book.id === selectedBookId
    )

    if (book.creator.id !== userId && !bookIsInBookCase) {
      // Add book to bookcase
      const updateBookRes = await postUpdateBook({
        id: selectedBookId,
        connectCustomers: [userId],
      })

      if (!updateBookRes.success) {
        throw new 'add book to bookcase failed'()
      }
    }

    const [payload, post] = (() => {
      if (typeof data.resourceId === 'string') {
        const [type, id] = data.resourceId.split('-')

        if (type === 'demo') {
          return [
            {
              id,
              books: { connect: [selectedBookId] },
            },
            selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
              ? postUpdateDemoSchoolClass
              : postUpdateDemoSchoolGroup,
          ]
        } else {
          return [
            {
              id: data.resourceId,
              books: { connect: [{ id: selectedBookId, sharedBy: userId }] },
            },
            selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
              ? postUpdateSchoolClass
              : postUpdateSchoolGroup,
          ]
        }
      }
    })()

    const res = await post(
      payload,
      'Bok delad.',
      'Nåt gick fel när boken skulle delas.'
    )

    if (res.success) dispatch(closePanel(PANELS.SHARE_BOOK))
  }

  const classOptions = !isLoading && [
    ...(demoSchoolClassOptions ?? []),
    ...(schoolClassOptions ?? []),
  ]
  const groupOptions = !isLoading && [
    ...(demoSchoolGroupOptions ?? []),
    ...(schoolGroupOptions ?? []),
  ]

  const noClassesOrGroups =
    (!classOptions || classOptions?.length === 0) &&
    (!groupOptions || groupOptions.length === 0)

  return (
    <>
      <div className={'relative'}>
        <div className='absolute top-0 right-0'>
          <Button
            handleClick={() => {
              dispatch(closePanel(PANELS.SHARE_BOOK))
            }}
            size={BUTTON_SIZE.NO_PADDING}
            icon={'Close'}
          ></Button>
        </div>
      </div>

      {selectedBookId && (
        <div className={'flex flex-col items-center'}>
          <Divider size={DIVIDER_SIZE.SMALL} />

          {isLoading && <Loader />}
          {error && <Text>Någonting gick fel när vi skulle hämta klasser</Text>}

          {!isLoading && noClassesOrGroups && (
            <div className={'flex flex-col items-center'}>
              <Heading headingLevel={HEADING_TYPE.H3} size={TEXT_SIZES.MEDIUM}>
                {
                  'Du har inga klasser eller grupper. Gå till "mitt lärarrum" för att lägga till en klass eller skapa en grupp.'
                }
              </Heading>

              <Divider size={DIVIDER_SIZE.BASE} />

              <Button
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.LARGE}
                handleClick={() => {
                  dispatch(closePanel(PANELS.SHARE_BOOK))
                  navigate('/lararrum')
                }}
              >
                Till mitt lärarrum
              </Button>
            </div>
          )}

          {(groupOptions?.length > 0 || classOptions?.length > 0) && (
            <>
              {selectedBookTitle && (
                <Heading
                  headingLevel={HEADING_TYPE.H2}
                  size={TEXT_SIZES.XLARGE}
                >
                  {selectedBookTitle}
                </Heading>
              )}

              <div className={'my-8'}>
                <Button
                  disabled={
                    demoSchoolClassOptions?.length === 0 &&
                    schoolClassOptions?.length === 0
                  }
                  type={
                    selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
                      ? BUTTON_TYPE.PRIMARY
                      : BUTTON_TYPE.SECONDARY
                  }
                  size={BUTTON_SIZE.MEDIUM}
                  margin
                  handleClick={() => {
                    setSelectedResourceType(SCHOOL_RESOURCE_TYPES.CLASS)
                  }}
                >
                  Dela med klass
                </Button>

                <Button
                  disabled={
                    demoSchoolGroupOptions?.length === 0 &&
                    schoolGroupOptions?.length === 0
                  }
                  type={
                    selectedResourceType === SCHOOL_RESOURCE_TYPES.GROUP
                      ? BUTTON_TYPE.PRIMARY
                      : BUTTON_TYPE.SECONDARY
                  }
                  size={BUTTON_SIZE.MEDIUM}
                  margin
                  handleClick={() => {
                    setSelectedResourceType(SCHOOL_RESOURCE_TYPES.GROUP)
                  }}
                >
                  Dela med grupp
                </Button>
              </div>

              <Form
                onSubmit={handleSubmit(onSubmit)}
                className={'w-full max-w-md flex flex-col items-center'}
              >
                <Text bold extraClasses={'self-start'} htmlFor='resourceId'>
                  {selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
                    ? 'Klass'
                    : 'Grupp'}
                </Text>
                <Form.Select
                  name='resourceId'
                  id='resourceId'
                  onChange={(event) => {
                    // console.log('🚀 ~ file: index.js ~ line 310 ~ event', event.target.value)

                    return setSelectedResourceValue(event.target.value)
                  }}
                  ref={register({ required: true })}
                  options={[
                    {
                      value: '',
                      name:
                        selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
                          ? 'Välj klass'
                          : 'Välj grupp',
                    },
                    ...(selectedResourceType === SCHOOL_RESOURCE_TYPES.CLASS
                      ? classOptions
                      : groupOptions),
                  ]}
                />
                {errors.resourceId && (
                  <Form.ErrorMessage>Klass behövs</Form.ErrorMessage>
                )}

                <Divider size={DIVIDER_SIZE.SMALL} />

                <ButtonWithLoader
                  type={BUTTON_TYPE.PRIMARY}
                  isLoading={isPosting || isPostingUpdateBook}
                  disabledText={'Dela'}
                  enabledText={'Dela'}
                  disabled={
                    userHasSubmitted ||
                    isPosting ||
                    isPostingUpdateBook ||
                    !selectedResourceValue
                  }
                />
              </Form>
            </>
          )}
        </div>
      )}
    </>
  )
}

export default ShareBookPanel
